import { computed, Injectable, signal } from '@angular/core';
import { ProviderName } from '@libs/shared/interfaces/glucose/dtos';
import { objectToQueryParams } from '@libs/shared/utils';

@Injectable({
  providedIn: 'root',
})
export class ApiUrlService {
  public contentCreators = computed(() => `${this._baseUrl()}/content-creators`);
  public knowledgeBits = computed(() => `${this._baseUrl()}/knowledge-bits`);

  private _baseUrl = signal('');
  private _dbBaseUrl?: string;

  /**
   * DB Base URL
   */
  public get dbBaseUrl(): string | undefined {
    return this._dbBaseUrl;
  }

  /**
   * DB App
   */
  public get dbApp() {
    return (): string => {
      return `${this.dbBaseUrl}/app`;
    };
  }

  /**
   * Base URL
   */
  public get baseUrl(): string {
    return this._baseUrl();
  }

  /**
   * UploadSpeedTest endpoint
   */
  public get uploadSpeedTest() {
    return (): string => {
      return `${this.baseUrl}/upload-speed-test`;
    };
  }

  /**
   * DownloadSpeedTest endpoint
   */
  public get downloadSpeedTest() {
    return (): string => {
      return `${this.baseUrl}/static/speed-test`;
    };
  }

  /**
   * Login endpoint
   */
  public get authLogin() {
    return (): string => {
      return `${this.baseUrl}/auth/login`;
    };
  }

  /**
   * Register biometry endpoint
   */
  public get authRegisterBiometry() {
    return (): string => {
      return `${this.baseUrl}/auth/register-biometry`;
    };
  }

  /**
   * Login with biometry endpoint
   */
  public get authLoginWithBiometry() {
    return (): string => {
      return `${this.baseUrl}/auth/login-with-biometry`;
    };
  }

  /**
   * Has passkey endpoint
   */
  public get authHasPasskey() {
    return (): string => {
      return `${this.baseUrl}/auth/has-passkey`;
    };
  }

  /**
   * Delete passkey endpoint
   */
  public get authDeletePasskey() {
    return (): string => {
      return `${this.baseUrl}/auth/delete-passkey`;
    };
  }

  /**
   * Passkey registration options endpoint
   */
  public get authPasskeyRegistrationOptions() {
    return (): string => {
      return `${this.baseUrl}/auth/passkey-registration/options`;
    };
  }

  /**
   * Passkey registration verification endpoint
   */
  public get authPasskeyRegistrationVerification() {
    return (): string => {
      return `${this.baseUrl}/auth/passkey-registration/verify`;
    };
  }

  /**
   * Passkey authentication options endpoint
   */
  public get authPasskeyAuthenticationOptions() {
    return (): string => {
      return `${this.baseUrl}/auth/passkey-authentication/options`;
    };
  }

  /**
   * Passkey authentication verification endpoint
   */
  public get authPasskeyAuthenticationVerification() {
    return (): string => {
      return `${this.baseUrl}/auth/passkey-authentication/verify`;
    };
  }

  /**
   * Password reset with passkey endpoint
   */
  public get authResetPasswordWithPasskey() {
    return (): string => {
      return `${this.baseUrl}/auth/passkey-authentication/password-reset`;
    };
  }

  /**
   * Consents endpoint
   */
  public get authConsents() {
    return (): string => {
      return `${this.baseUrl}/auth/consents`;
    };
  }

  /**
   * Register endpoint
   */
  public get authRegister() {
    return (): string => {
      return `${this.baseUrl}/auth/register`;
    };
  }

  /**
   * Refresh token endpoint
   */
  public get authRefresh() {
    return (): string => {
      return `${this.baseUrl}/auth/refresh`;
    };
  }

  /**
   * Logout endpoint
   */
  public get authLogout() {
    return (): string => {
      return `${this.baseUrl}/auth/logout`;
    };
  }

  /**
   * Login endpoint
   */
  public get authLogoutOthers() {
    return (): string => {
      return `${this.baseUrl}/auth/logout-others`;
    };
  }

  /**
   * Login endpoint
   */
  public get authRequestDeletion() {
    return (): string => {
      return `${this.baseUrl}/auth/request-deletion`;
    };
  }

  /**
   * Request password reset endpoint
   */
  public get authRequestPasswordReset() {
    return (): string => {
      return `${this.baseUrl}/auth/forgot-password`;
    };
  }

  /**
   * Change password endpoint
   */
  public get authChangePassword() {
    return (): string => {
      return `${this.baseUrl}/auth/password-change`;
    };
  }

  /**
   * Change email endpoint
   */
  public get authUpdateEmail() {
    return (): string => {
      return `${this.baseUrl}/auth/change-email`;
    };
  }

  /**
   * Password reset endpoint
   */
  public get authResetPassword() {
    return (): string => {
      return `${this.baseUrl}/auth/password-reset`;
    };
  }

  public get admins() {
    return (): string => {
      return `${this.baseUrl}/admins`;
    };
  }

  public get adminById() {
    return (id: string): string => {
      return `${this.baseUrl}/admins/${id}`;
    };
  }

  /**
   * Password reset endpoint
   */
  public get fileById() {
    return (id: string): string => {
      return `${this.baseUrl}/files/${id}`;
    };
  }

  /**
   * Password reset endpoint
   */
  public get pushTokenById() {
    return (id: string): string => {
      return `${this.baseUrl}/push-tokens/${id}`;
    };
  }

  public get fileDownload() {
    return (id: string): string => {
      return `${this.baseUrl}/files/${id}/download`;
    };
  }

  /**
   * Copy img id and paste image with a new id
   * returns id of new img
   */
  public get duplicateImgWithId() {
    return (originalId: string): string => {
      return `${this.baseUrl}/files/${originalId}/duplicate`;
    };
  }

  /**
   * Password reset endpoint
   */
  public get fileDownloadClient() {
    return (id: string, queryParams?: { [key: string]: string | number }): string => {
      return `${this.baseUrl}/files/${id}/download${queryParams ? `?${objectToQueryParams(queryParams)}` : ''}`;
    };
  }

  /**
   * File upload endpoint
   */
  public get fileUpload() {
    return (): string => {
      return `${this.baseUrl}/files/upload`;
    };
  }

  /**
   *  Steps Upload endpoint
   */
  public get healthStepsEntries() {
    return (): string => {
      return `${this.baseUrl}/health/steps`;
    };
  }

  /**
   *  Bodymass Upload endpoint
   */
  public get healthBodyMassEntries() {
    return (): string => {
      return `${this.baseUrl}/health/bodymass`;
    };
  }

  /**
   *  Workouts Upload endpoint
   */
  public get healthWorkoutsEntries() {
    return (): string => {
      return `${this.baseUrl}/health/workouts`;
    };
  }

  /**
   *  Thryve status endpoint
   */
  public get healthThryveStatus() {
    return (userId: string): string => {
      return `${this.baseUrl}/health/thryve/${userId}/status`;
    };
  }

  /**
   *  Thryve status endpoint
   */
  public get healthThryveSourceConnectionUrl() {
    return (userId: string, dataSource: string): string => {
      return `${this.baseUrl}/health/thryve/${userId}/connections/${dataSource}/connection-url`;
    };
  }

  /**
   *  Thryve status endpoint
   */
  public get healthThryveRevokeConnection() {
    return (userId: string, dataSource: string): string => {
      return `${this.baseUrl}/health/thryve/${userId}/connections/${dataSource}`;
    };
  }

  /**
   * User update endpoint
   */
  public get userById() {
    return (id: string): string => {
      return `${this.baseUrl}/users/${id}`;
    };
  }

  /**
   * User update endpoint
   */
  public get userGlucoseConnectionStatus() {
    return (): string => {
      return `${this.baseUrl}/user/glucose/connection-status`;
    };
  }

  public get userGlucoseOverrideEmail() {
    return (): string => {
      return `${this.baseUrl}/user/glucose/override-email`;
    };
  }

  public get userGlucoseDisconnectSource() {
    return (sourceId: string): string => {
      return `${this.baseUrl}/user/glucose/sources/${sourceId}`;
    };
  }

  public get glucoseTimeframes() {
    return (userId: string): string => {
      return `${this.baseUrl}/users/${userId}/glucose/glucose-timeframes`;
    };
  }

  public get refreshUserGlucose() {
    return (): string => {
      return `${this.baseUrl}/users/glucose/refresh`;
    };
  }

  public get currentUserBloodGlucoseUnit() {
    return (): string => {
      return `${this.baseUrl}/user/glucose/blood-glucose-unit`;
    };
  }

  public get replacementSensorOrder() {
    return (id?: string, isExtended = false): string => {
      const params = new URLSearchParams();

      if (id) {
        params.append('id', id);
      }

      if (isExtended) {
        params.append('isExtended', 'true');
      }

      return params.size > 0
        ? `${this.baseUrl}/static/replacement-sensor-order?${params.toString()}`
        : `${this.baseUrl}/static/replacement-sensor-order`;
    };
  }

  public get createReplacementSensorOrder() {
    return (): string => {
      return `${this.baseUrl}/replacement-sensor-orders`;
    };
  }

  /**
   * Get voucher info
   */
  public get voucherInfo() {
    return (code: string, userId: string): string => {
      return `${this.baseUrl}/vouchers/${code}?userId=${userId}`;
    };
  }

  /**
   * Activate voucher
   */
  public get voucherActivate() {
    return (code: string): string => {
      return `${this.baseUrl}/vouchers/${code}/activate`;
    };
  }

  /**
   * End enabled guide run in last phase
   */
  public get endEnabledGuideRunInLastPhase() {
    return (): string => {
      return `${this.baseUrl}/vouchers/endEnabledGuideRunInLastPhase`;
    };
  }

  /**
   * Send guide run events
   */
  public get events() {
    return (): string => {
      return `${this.baseUrl}/events`;
    };
  }

  public get meals() {
    return (): string => {
      return `${this.baseUrl}/meals`;
    };
  }

  public get mealById() {
    return (id: string): string => {
      return `${this.baseUrl}/meals/${id}`;
    };
  }

  public get mealImage() {
    return (id: string): string => {
      return `${this.baseUrl}/meals/${id}/image`;
    };
  }

  public get aiMealDetection() {
    return (): string => {
      return `${this.baseUrl}/meals/ai-meal-detection`;
    };
  }

  /**
   * Foods
   */
  public get foods() {
    return (): string => {
      return `${this.baseUrl}/foods`;
    };
  }

  /**
   * Get voucher info
   */
  public get contactSendMessage() {
    return (): string => {
      return `${this.baseUrl}/contact/send-message`;
    };
  }

  public get getAllAvailableZammadGroups() {
    return (): string => {
      return `${this.baseUrl}/contact/list-groups`;
    };
  }

  /**
   * Get random lesson
   */
  public get randomLesson() {
    return (): string => {
      return `${this.baseUrl}/lessons/random`;
    };
  }

  /**
   * Get all lessons
   */
  public get allLessons() {
    return (): string => {
      return `${this.baseUrl}/lessons`;
    };
  }

  /**
   * Get lesson according to an id
   */
  public get lessonById() {
    return (id: string): string => {
      return `${this.baseUrl}/lessons/${id}`;
    };
  }

  public get activityTypes() {
    return (): string => './assets/data/activities.json';
  }

  public get challenges() {
    return (): string => {
      return `${this.baseUrl}/challenges`;
    };
  }

  public get challengeById() {
    return (id: string): string => {
      return `${this.baseUrl}/challenges/${id}`;
    };
  }

  public get challengeTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/challenges/${id}/translations`;
    };
  }

  public get challengeTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/challenges/${id}/translations/${language}`;
    };
  }

  public get challengeActions() {
    return (id: string): string => {
      return `${this.baseUrl}/challenges/${id}/actions`;
    };
  }

  public get challengeActionById() {
    return (id: string, actionId: string): string => {
      return `${this.baseUrl}/challenges/${id}/actions/${actionId}`;
    };
  }

  public get challengeAdditionalContents() {
    return (id: string): string => {
      return `${this.baseUrl}/challenges/${id}/additional-contents`;
    };
  }

  public get challengeAdditionalContentById() {
    return (id: string, additionalContentId: string): string => {
      return `${this.baseUrl}/challenges/${id}/additional-contents/${additionalContentId}`;
    };
  }

  public get challengeCategories() {
    return (): string => {
      return `${this.baseUrl}/challenge-categories`;
    };
  }

  public get challengeCategoryById() {
    return (id: string): string => {
      return `${this.baseUrl}/challenge-categories/${id}`;
    };
  }

  public get challengeCategoryTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/challenge-categories/${id}/translations/${language}`;
    };
  }

  public get subChallenges() {
    return (challengeId: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges`;
    };
  }

  public get subChallengeById() {
    return (challengeId: string, subChallengeId: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges/${subChallengeId}`;
    };
  }

  public get subChallengeActions() {
    return (challengeId: string, subChallengeId: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges/${subChallengeId}/actions`;
    };
  }

  public get subChallengeActionById() {
    return (challengeId: string, subChallengeId: string, actionId: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges/${subChallengeId}/actions/${actionId}`;
    };
  }

  public get subChallengeTranslations() {
    return (challengeId: string, subChallengeId: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges/${subChallengeId}/translations`;
    };
  }

  public get subChallengeTranslationByLanguage() {
    return (challengeId: string, subChallengeId: string, language: string): string => {
      return `${this.baseUrl}/challenges/${challengeId}/sub-challenges/${subChallengeId}/translations/${language}`;
    };
  }

  //#region Lessons
  public get lessons() {
    return (): string => {
      return `${this.baseUrl}/lessons`;
    };
  }

  public get lessonTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/lessons/${id}/translations`;
    };
  }

  public get lessonTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/lessons/${id}/translations/${language}`;
    };
  }

  public get lessonRandom() {
    return (): string => {
      return `${this.baseUrl}/lessons/random`;
    };
  }

  public get lessonCategories() {
    return (): string => {
      return `${this.baseUrl}/lesson-categories`;
    };
  }

  public get lessonCategoryById() {
    return (id: string): string => {
      return `${this.baseUrl}/lesson-categories/${id}`;
    };
  }

  public get lessonCategoryTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/lesson-categories/${id}/translations/${language}`;
    };
  }
  //#endregion

  //#region recipes
  public get recipes() {
    return (categoryInternalName?: string): string => {
      return `${this.baseUrl}/recipes${categoryInternalName ? `?categoryInternalName=${categoryInternalName}` : ''}`;
    };
  }

  public get recipeById() {
    return (id: string): string => {
      return `${this.baseUrl}/recipes/${id}`;
    };
  }

  public get recipeTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/recipes/${id}/translations`;
    };
  }

  public get recipeTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/recipes/${id}/translations/${language}`;
    };
  }

  public get recipeRandom() {
    return (): string => {
      return `${this.baseUrl}/recipes/random`;
    };
  }

  public get recipeCategories() {
    return (): string => {
      return `${this.baseUrl}/recipe-categories`;
    };
  }

  public get recipeCategoryById() {
    return (id: string): string => {
      return `${this.baseUrl}/recipe-categories/${id}`;
    };
  }

  public get recipeCategoryTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/recipe-categories/${id}/translations/${language}`;
    };
  }

  public get recipeIngredients() {
    return (id: string): string => {
      return `${this.baseUrl}/recipes/${id}/ingredients`;
    };
  }

  public get recipeIngredientById() {
    return (id: string, ingredientId: string): string => {
      return `${this.baseUrl}/recipes/${id}/ingredients/${ingredientId}`;
    };
  }

  public get recipeIngredientTranslations() {
    return (id: string, ingredientId: string): string => {
      return `${this.baseUrl}/recipes/${id}/ingredients/${ingredientId}/translations`;
    };
  }

  public get recipeIngredientTranslationByLanguage() {
    return (id: string, ingredientId: string, language: string): string => {
      return `${this.baseUrl}/recipes/${id}/ingredients/${ingredientId}/translations/${language}`;
    };
  }
  //#endregion

  //#region questionnaires
  public get questionnaires() {
    return (): string => {
      return `${this.baseUrl}/questionnaires`;
    };
  }

  public get questionnaireById() {
    return (idOrInternalName: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(idOrInternalName)}`;
    };
  }

  public get questionnaireByInternalName() {
    return (internalName: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(internalName)}`;
    };
  }

  public get questionnaireByInternalNameAndFormatAndLanguage() {
    return (internalName: string, format: 'onboarding' | 'anamnesis', language: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(internalName)}?format=${format}&language=${language}`;
    };
  }

  public get questionnaireTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(id)}/translations`;
    };
  }

  public get questionnaireTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(id)}/translations/${language}`;
    };
  }

  public get questionnaireToQuestions() {
    return (questionnaireInternalName: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(questionnaireInternalName)}/questions`;
    };
  }

  public get questionnaireToQuestionByInternalName() {
    return (questionnaireInternalName: string, questionInternalName: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(
        questionnaireInternalName,
      )}/questions/${encodeURIComponent(questionInternalName)}`;
    };
  }

  public get questionnaireQuestions() {
    return (): string => {
      return `${this.baseUrl}/questionnaire-questions/`;
    };
  }

  public get questionnaireQuestionOrder() {
    return (id: string): string => {
      return `${this.baseUrl}/questionnaires/${encodeURIComponent(id)}/question-order`;
    };
  }

  public get questionnaireQuestionById() {
    return (id: string): string => {
      return `${this.baseUrl}/questionnaire-questions/${encodeURIComponent(id)}`;
    };
  }

  public get questionnaireQuestionTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/questionnaire-questions/${encodeURIComponent(id)}/translations`;
    };
  }

  public get questionnaireQuestionTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/questionnaire-questions/${encodeURIComponent(id)}/translations/${language}`;
    };
  }
  //#endregion

  //#region Videos
  public get videos() {
    return (): string => {
      return `${this.baseUrl}/videos`;
    };
  }

  public get videoById() {
    return (id: string): string => {
      return `${this.baseUrl}/videos/${id}`;
    };
  }

  public get videoConvertPlaylist() {
    return (url: string): string => {
      return `${this.baseUrl}/videos/convert-playlist?url=${url}`;
    };
  }

  public get videoTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/videos/${id}/translations`;
    };
  }

  public get videoTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/videos/${id}/translations/${language}`;
    };
  }

  public get videoCategories() {
    return (): string => {
      return `${this.baseUrl}/video-categories`;
    };
  }

  public get videoCategoryById() {
    return (id: string): string => {
      return `${this.baseUrl}/video-categories/${id}`;
    };
  }

  public get videoCategoryTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/video-categories/${id}/translations/${language}`;
    };
  }
  //#endregion

  //#region guides
  public get guides() {
    return (): string => {
      return `${this.baseUrl}/guides`;
    };
  }

  public get guideById() {
    return (id: string): string => {
      return `${this.baseUrl}/guides/${id}`;
    };
  }

  public get guideTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/guides/${id}/translations/${language}`;
    };
  }

  public get guidesGuideCriteriaExcludesByID() {
    return (guideId: string, guideCriteriaExcludesId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/guide-criteria-excludes/${guideCriteriaExcludesId}`;
    };
  }

  public get guideCriteriaExcludes() {
    return (): string => {
      return `${this.baseUrl}/guide-criteria-excludes`;
    };
  }

  public get guideCriteriaExcludesById() {
    return (id: string): string => {
      return `${this.baseUrl}/guide-criteria-excludes/${id}`;
    };
  }

  public get guideCriteriaExcludesTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/guide-criteria-excludes/${id}/translations/${language}`;
    };
  }

  public get guideGuidePhasesById() {
    return (guideId: string, guidePhaseId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/guide-phases/${guidePhaseId}`;
    };
  }

  public get guideGuidePhaseOrderById() {
    return (guideId: string, guidePhaseId: string, order: number): string => {
      return `${this.baseUrl}/guides/${guideId}/guide-phases/${guidePhaseId}/${order}`;
    };
  }

  public get guideRecipeCategoriesById() {
    return (guideId: string, recipeCategoryId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/recipe-categories/${recipeCategoryId}`;
    };
  }

  public get guideRecipeCategoryOrderById() {
    return (guideId: string, recipeCategoryId: string, order: number): string => {
      return `${this.baseUrl}/guides/${guideId}/recipe-categories/${recipeCategoryId}/${order}`;
    };
  }

  public get guideLessonCategoriesById() {
    return (guideId: string, lessonCategoryId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/lesson-categories/${lessonCategoryId}`;
    };
  }

  public get guideLessonCategoryOrderById() {
    return (guideId: string, lessonCategoryId: string, order: number): string => {
      return `${this.baseUrl}/guides/${guideId}/lesson-categories/${lessonCategoryId}/${order}`;
    };
  }

  public get guideVideoCategoriesById() {
    return (guideId: string, videoCategoryId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/video-categories/${videoCategoryId}`;
    };
  }

  public get guideVideoCategoryOrderById() {
    return (guideId: string, videoCategoryId: string, order: number): string => {
      return `${this.baseUrl}/guides/${guideId}/video-categories/${videoCategoryId}/${order}`;
    };
  }

  public get guideCopyCategoriesFrom() {
    return (guideId: string, sourceGuideId: string): string => {
      return `${this.baseUrl}/guides/${guideId}/copy-categories-from/${sourceGuideId}`;
    };
  }
  //#endregion

  //#region GuideRuns

  public get disableActiveGuideRun() {
    return (): string => {
      return `${this.baseUrl}/guide-runs/active/disable`;
    };
  }
  //#endregion

  //#region GuidePhases
  public get guidePhases() {
    return (): string => {
      return `${this.baseUrl}/guide-phases`;
    };
  }

  public get guidePhaseTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/guide-phases/${id}/translations/${language}`;
    };
  }

  public get guidePhaseById() {
    return (id: string): string => {
      return `${this.baseUrl}/guide-phases/${id}`;
    };
  }

  public get guidePhaseConditionOnGuidePhaseById() {
    return (guidePhaseId: string, guidePhaseConditionId: string): string => {
      return `${this.baseUrl}/guide-phases/${guidePhaseId}/guide-phase-conditions/${guidePhaseConditionId}`;
    };
  }
  //#endregion

  //#region GuidePhaseConditions
  public get guidePhaseConditions() {
    return (): string => {
      return `${this.baseUrl}/guide-phase-conditions`;
    };
  }

  public get guidePhaseConditionById() {
    return (id: string): string => {
      return `${this.baseUrl}/guide-phase-conditions/${id}`;
    };
  }
  //#endregion

  //#region User offset
  public get userOffset() {
    return (): string => {
      return `${this.baseUrl}/user-offset`;
    };
  }
  //#endregion

  //#region User Guide Run Logic
  public get userGuideRunPhaseConditionStatusUpdate() {
    return (): string => `${this.baseUrl}/user/guide-runs/active/phase-condition-status/update`;
  }

  public get userGuideRunPhaseConditionStatusRejectPhase() {
    return (): string => `${this.baseUrl}/user/guide-runs/active/phase-condition-status/rejected`;
  }

  public get userGuideRunTransitionToPhase() {
    return (): string => `${this.baseUrl}/user/guide-runs/active/transition-to-phase`;
  }

  //#endregion

  //#region Goal
  public get goals() {
    return (): string => {
      return `${this.baseUrl}/goals`;
    };
  }

  public get goalById() {
    return (id: string): string => `${this.baseUrl}/goals/${id}`;
  }

  public get goalTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/goals/${id}/translations/${language}`;
    };
  }
  //#endregion

  //#region TestMeal
  public get testMeals() {
    return (): string => {
      return `${this.baseUrl}/test-meals`;
    };
  }

  public get testMealById() {
    return (id: string): string => {
      return `${this.baseUrl}/test-meals/${id}`;
    };
  }

  public get testMealTranslations() {
    return (id: string): string => {
      return `${this.baseUrl}/test-meals/${id}/translations`;
    };
  }

  public get testMealTranslationByLanguage() {
    return (id: string, language: string): string => {
      return `${this.baseUrl}/test-meals/${id}/translations/${language}`;
    };
  }

  public get testMealIngredients() {
    return (id: string): string => {
      return `${this.baseUrl}/test-meals/${id}/ingredients`;
    };
  }

  public get testMealIngredientById() {
    return (id: string, ingredientId: string): string => {
      return `${this.baseUrl}/test-meals/${id}/ingredients/${ingredientId}`;
    };
  }

  public get testMealIngredientTranslations() {
    return (id: string, ingredientId: string): string => {
      return `${this.baseUrl}/test-meals/${id}/ingredients/${ingredientId}/translations`;
    };
  }

  public get testMealIngredientTranslationByLanguage() {
    return (id: string, ingredientId: string, language: string): string => {
      return `${this.baseUrl}/test-meals/${id}/ingredients/${ingredientId}/translations/${language}`;
    };
  }
  //#endregion

  //#region diabetes therapy report
  public get therapyReport() {
    return (guideRunId: string, language: string, name?: string): string => {
      let basisParams = `?lang=${language}`;
      if (name) {
        basisParams = basisParams.concat(`&name=${encodeURIComponent(name)}`);
      }

      return `${this.baseUrl}/reports/therapy-report/${guideRunId}${basisParams}`;
    };
  }

  //#region Migraine2 SinCephalea
  public get monthlyReport() {
    return (guideRunId: string, language: string, name?: string): string => {
      const encodedName = name ? encodeURIComponent(name) : '';
      const nameSegment = encodedName === '' ? '' : `/${encodedName}`;

      return `${this.baseUrl}/reports/monthly-report/${guideRunId}${nameSegment}?lang=${language}`;
    };
  }

  public get fhirExportGlucura() {
    return (): string => {
      return `${this.baseUrl}/reports/fhir`;
    };
  }

  public get fhirExportSC() {
    return (): string => {
      return `${this.baseUrl}/reports/sc-fhir`;
    };
  }

  public get fhirExport() {
    return (path: 'fhir' | 'sc-fhir'): string => {
      return `${this.baseUrl}/reports/${path}`;
    };
  }

  public get dataExport() {
    return (language: string, name: string): string => {
      return `${this.baseUrl}/reports/data-export/${name}?lang=${language}`;
    };
  }
  //#endregion

  //#region Glucose Auth
  public get glucoseLogin() {
    return (provider: ProviderName): string => {
      return `${this.baseUrl}/auth/${provider}/login`;
    };
  }
  //#endregion

  //#region OIDC
  public get idpsOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/idps`;
    };
  }

  public get parOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/par`;
    };
  }

  public get registerOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/register`;
    };
  }

  public get loginOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/login`;
    };
  }

  public get linkOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/link`;
    };
  }

  public get unlinkOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/unlink`;
    };
  }

  public get changeEmailOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/change-email`;
    };
  }

  public get requestDeletionOidc() {
    return (): string => {
      return `${this.baseUrl}/oidc/request-deletion`;
    };
  }
  //#endregion

  //#region ePA
  public get connectEpa() {
    return (): string => {
      return `${this.baseUrl}/epa`;
    };
  }

  public get disableEpa() {
    return (): string => {
      return `${this.baseUrl}/epa/disable`;
    };
  }
  //#endregion

  //#region migration
  public get mayoMigration() {
    return (): string => {
      return `${this.baseUrl}/migrations/mayo`;
    };
  }
  //#endregion

  //#region Business Process
  public get businessProcesses() {
    return (userId: string): string => {
      return `${this.baseUrl}/business-processes/${userId}`;
    };
  }
  //#endregion

  //#region diga-code
  public get validateDigaCode() {
    return (): string => {
      return `${this.baseUrl}/noventi/diga-code/validate`;
    };
  }

  public get redeemDigaCode() {
    return (): string => {
      return `${this.baseUrl}/noventi/diga-code/redeem`;
    };
  }
  //#endregion

  //#region doctor
  public get getDoctorSearchList() {
    return (): string => {
      return `${this.baseUrl}/pipedrive/doctor`;
    };
  }

  public get getDoctorDetails() {
    return (id: string): string => {
      return `${this.baseUrl}/pipedrive/doctor/${id}`;
    };
  }

  public get createDoctor() {
    return (): string => {
      return `${this.baseUrl}/pipedrive/doctor`;
    };
  }

  public get updateDoctor() {
    return (id: string): string => {
      return `${this.baseUrl}/pipedrive/doctor/${id}`;
    };
  }

  public get createActivity() {
    return (id: string): string => {
      return `${this.baseUrl}/pipedrive/doctor/${id}/activity`;
    };
  }
  //#endregion

  //#region order-process
  public get createOrder() {
    return (): string => {
      return `${this.baseUrl}/order-process`;
    };
  }
  //#endregion

  public init(baseUrl: string, dbBaseUrl?: string): void {
    this._baseUrl.set(baseUrl);
    this._dbBaseUrl = dbBaseUrl;
  }

  /**
   * Checks if a URL requires authentication
   * @param {string} url URL to be checked
   * @returns {boolean}
   */
  public noAuthRequired(url: string): boolean {
    const urlsWithoutAuthentication = [
      this.authLogin(),
      this.authRegister(),
      this.authRequestPasswordReset(),
      this.authResetPassword(),
    ];

    return urlsWithoutAuthentication.includes(url);
  }

  /**
   * Checks if a URL requires authentication
   * @param {string} url URL to be checked
   * @returns {boolean}
   */
  public skipAuthErrorHandling(url: string): boolean {
    const urlsWithoutAuthErrorHandling = [
      this.authRequestDeletion(),
      this.authChangePassword(),
      this.authUpdateEmail(),
      this.changeEmailOidc(),
      this.registerOidc(),
      this.loginOidc(),
      this.linkOidc(),
      this.unlinkOidc(),
      this.authLoginWithBiometry(),
      this.authPasskeyAuthenticationVerification(),
      this.authResetPasswordWithPasskey(),
    ];

    return urlsWithoutAuthErrorHandling.includes(url);
  }

  /**
   * Checks if in customer view post requests are allowed
   * @param {string} url URL to be checked
   * @returns {boolean}
   */
  public customerViewPostWhitelist(url: string): boolean {
    const urlsWithoutPostBlockInCustomerView = [this.authLogin(), this.authRefresh(), this.authLogout()];

    return urlsWithoutPostBlockInCustomerView.includes(url);
  }
}
